import { extractLinks } from '../../reports/lib/extract-links';

/**
 * Чистка вставки в поле «Ссылки на публикации»: из вставленного остаются
 * ссылки, остальной текст пропадает сам.
 *
 * Ссылки часто копируют из таблицы статистики — строка на публикацию, рядом
 * тип, дата и цифры:
 *
 *   Ссылка  Тип  Дата  Просмотры …
 *   tiktok.com/@tokayev.news/video/768…  VIDEO  23.09.2026, 18:10:00  16 539 …
 *
 * Шапка такой таблицы в разборе стала бы «темой», а цифры в строке только
 * мешают читать поле. Поэтому, если хоть в одной строке рядом со ссылкой есть
 * что-то ещё, — это таблица, и из неё берутся только ссылки.
 *
 * Иначе вставлена разметка оператора (см. parse-seeding-input): тема строкой
 * над ссылками и пустые строки между темами. Тему и пустые строки не трогаем,
 * выкидываем только текст под ссылкой — подпись к посту, скопированную из
 * чата вместе с ней.
 */

// Ссылка без схемы: «tiktok.com/@user/video/1», «youtu.be/abc». extractLinks
// таких не узнаёт (кроме instagram.com), и строка молча пропала бы целиком.
const BARE_LINK = /^(?:[\w-]+\.)+[a-z]{2,}\/\S+$/i;
const TRAILING_PUNCTUATION = /[.,;:!?)\]»"']+$/;
const LEADING_PUNCTUATION = /^[([«"']+/;

function linksOf(line: string): string[] {
  const found = extractLinks(line);
  for (const raw of line.split(/\s+/)) {
    const word = raw.replace(LEADING_PUNCTUATION, '').replace(TRAILING_PUNCTUATION, '');
    if (!found.includes(word) && BARE_LINK.test(word)) found.push(word);
  }
  // Схему дописываем, чтобы разбор и счётчик под полем увидели ссылку.
  return found.map((url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`));
}

/** Что-то кроме ссылок в строке: цифры, тип, дата. */
function hasTextBesideLinks(line: string): boolean {
  const words = line.split(/\s+/).filter(Boolean);
  const links = linksOf(line).length;
  return links > 0 && words.length > links;
}

function isBlank(line: string): boolean {
  return /^[ \t]*$/.test(line);
}

export function cleanPastedLinks(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const table = lines.some(hasTextBesideLinks);

  const out: string[] = [];
  for (const [index, line] of lines.entries()) {
    if (isBlank(line)) {
      out.push('');
      continue;
    }
    const links = linksOf(line);
    if (links.length > 0) {
      out.push(...links);
      continue;
    }
    if (table) continue;
    // Тема: начинает кусок (сверху пусто или начало вставки), а следом идёт ссылка.
    const startsChunk = index === 0 || isBlank(lines[index - 1]);
    const next = lines[index + 1];
    if (startsChunk && next !== undefined && linksOf(next).length > 0) out.push(line.trim());
  }
  return out.join('\n');
}
